import React, { useState, useEffect } from 'react';
import { Bell, X, Loader2 } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { UserRole } from '../types';

interface Notification {
    id: string;
    title: string;
    message: string;
    target_group: 'ALL' | 'WORKERS' | 'CUSTOMERS';
    created_at: string;
    is_read: boolean;
}

interface NotificationBellProps {
    userRole: UserRole;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ userRole }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [swipingId, setSwipingId] = useState<string | null>(null);
    const [swipeX, setSwipeX] = useState(0);

    useEffect(() => {
        fetchNotifications();

        // Real-time subscription - instant update from payload
        const channel = supabase
            .channel(`notifications_updates_${userRole}_${Date.now()}`)
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications' }, (payload) => {
                const notif = payload.new as Notification;
                if (!notif) return;
                // Check if this notification is for me
                const isForMe = notif.target_group === 'ALL' ||
                    (notif.target_group === 'WORKERS' && userRole === UserRole.WORKER) ||
                    (notif.target_group === 'CUSTOMERS' && userRole === UserRole.CUSTOMER);
                if (isForMe) {
                    setNotifications(prev => [notif, ...prev]);
                    setUnreadCount(prev => prev + 1);
                }
            })
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'notifications' }, () => {
                fetchNotifications();
            })
            .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'notifications' }, (payload) => {
                setNotifications(prev => prev.filter(n => n.id !== payload.old.id));
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [userRole]);

    const fetchNotifications = async () => {
        setIsLoading(true);
        try {
            // Determine which notifications to fetch based on user role
            let targetGroups = ['ALL'];
            if (userRole === UserRole.WORKER) {
                targetGroups.push('WORKERS');
            } else if (userRole === UserRole.CUSTOMER) {
                targetGroups.push('CUSTOMERS');
            }

            const { data, error } = await supabase
                .from('notifications')
                .select('*')
                .in('target_group', targetGroups)
                .order('created_at', { ascending: false })
                .limit(20);

            if (error) throw error;

            setNotifications(data || []);
            setUnreadCount((data || []).filter(n => !n.is_read).length);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Delete notification (auto-delete on view)
    const deleteNotification = async (id: string) => {
        try {
            await supabase
                .from('notifications')
                .delete()
                .eq('id', id);

            setNotifications(prev => prev.filter(n => n.id !== id));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    };

    const markAllRead = async () => {
        try {
            const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
            if (unreadIds.length === 0) return;

            await supabase
                .from('notifications')
                .update({ is_read: true })
                .in('id', unreadIds);

            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    // Handle swipe start
    const handleTouchStart = (e: React.TouchEvent, id: string) => {
        setSwipingId(id);
        setSwipeX(e.touches[0].clientX);
    };

    // Handle swipe move
    const handleTouchMove = (e: React.TouchEvent) => {
        if (!swipingId) return;
        const currentX = e.touches[0].clientX;
        const diff = currentX - swipeX;

        // Only allow left swipe (negative diff)
        if (diff < 0) {
            const element = document.getElementById(`notification-${swipingId}`);
            if (element) {
                element.style.transform = `translateX(${Math.max(diff, -100)}px)`;
            }
        }
    };

    // Handle swipe end
    const handleTouchEnd = (id: string) => {
        const element = document.getElementById(`notification-${id}`);
        if (!element) return;

        const transform = element.style.transform;
        const translateX = parseInt(transform.match(/-?\d+/)?.[0] || '0');

        // If swiped more than 50px, delete
        if (translateX < -50) {
            element.style.transform = 'translateX(-400px)';
            setTimeout(() => deleteNotification(id), 300);
        } else {
            // Reset position
            element.style.transform = 'translateX(0)';
        }

        setSwipingId(null);
        setSwipeX(0);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    return (
        <>
            {/* Bell Icon */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2.5 bg-slate-50 text-slate-600 hover:bg-slate-100 rounded-xl border border-slate-200 transition-all active:scale-95"
            >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Notification Panel */}
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/20 z-[90] lg:hidden"
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Panel */}
                    <div className="fixed top-16 right-4 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 z-[100] max-h-[80vh] flex flex-col animate-slideDown">
                        {/* Header */}
                        <div className="p-4 border-b border-slate-100 flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-2">
                                <Bell className="w-5 h-5 text-blue-600" />
                                <h3 className="font-black text-slate-900">Notifications</h3>
                            </div>
                            <div className="flex items-center gap-1">
                                {unreadCount > 0 && (
                                    <button
                                        onClick={markAllRead}
                                        className="text-[10px] font-bold text-blue-600 hover:bg-blue-50 px-2 py-1 rounded-md transition-colors"
                                    >
                                        Mark all read
                                    </button>
                                )}
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                                >
                                    <X className="w-4 h-4 text-slate-400" />
                                </button>
                            </div>
                        </div>

                        {/* Notifications List */}
                        <div className="flex-1 overflow-y-auto">
                            {isLoading ? (
                                <div className="flex items-center justify-center p-12">
                                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                                </div>
                            ) : notifications.length === 0 ? (
                                <div className="flex flex-col items-center justify-center p-12 text-center">
                                    <Bell className="w-12 h-12 text-slate-200 mb-3" />
                                    <p className="text-sm font-bold text-slate-400">No notifications yet</p>
                                    <p className="text-xs text-slate-300 mt-1">We'll notify you when something arrives</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-slate-50">
                                    {notifications.map((notification) => (
                                        <div
                                            key={notification.id}
                                            id={`notification-${notification.id}`}
                                            onClick={() => deleteNotification(notification.id)}
                                            onTouchStart={(e) => handleTouchStart(e, notification.id)}
                                            onTouchMove={handleTouchMove}
                                            onTouchEnd={() => handleTouchEnd(notification.id)}
                                            className={`p-4 hover:bg-slate-50 transition-all cursor-pointer relative overflow-hidden ${!notification.is_read ? 'bg-blue-50/50' : ''
                                                }`}
                                            style={{ transition: 'transform 0.3s ease' }}
                                        >
                                            {/* Notification content */}
                                            <div className="flex items-start gap-3 relative">
                                                {!notification.is_read && (
                                                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-1.5 shrink-0" />
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-bold text-sm text-slate-900 leading-tight mb-1">
                                                        {notification.title}
                                                    </h4>
                                                    <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">
                                                        {notification.message}
                                                    </p>
                                                    <p className="text-[10px] text-slate-400 font-bold mt-2">
                                                        {formatDate(notification.created_at)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
